import type { ProductVariantRecord } from '../db';

/**
 * A picture for a *generic* product.
 *
 * Only a variant carries a photograph — it is a specific box on a specific
 * shop's shelf — and a great many of the things this app shows are generic:
 * an "any store" item, a manual entry, everything under "Recent gekocht", and
 * the generic row of every local search hit. All of those were drawing the
 * lettered fallback tile even when the household owned a perfectly good picture
 * of the same product one row away, which on a grid of twelve reads as the
 * pictures being broken rather than absent.
 *
 * So a generic borrows one from its own variants. It is a *representative*
 * rather than a fact about the generic, which is why it is computed here at
 * render time and never written to a row: the day the household picks a
 * different carton of oat milk, the borrowed picture should follow, and a
 * stored one would not.
 *
 * First variant wins, and `db.product_variants.toArray()` returns them in
 * primary-key order, so both phones borrow the same picture for the same
 * product — an arbitrary choice is fine, an unstable one is not.
 */
export function imagesByGeneric(variants: ProductVariantRecord[]): Map<string, string> {
  const byGeneric = new Map<string, string>();
  for (const variant of variants) {
    if (variant.deleted_at || !variant.image_path) continue;
    if (!byGeneric.has(variant.generic_product_id)) {
      byGeneric.set(variant.generic_product_id, variant.image_path);
    }
  }
  return byGeneric;
}
